import { DndContext, DragOverlay, pointerWithin } from "@dnd-kit/core";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import Droppable from "components/Dnd/Droppable";
import { useEffect, useState } from "react";
import WorkflowUtils from "utils/WorkflowUtils";
import { CreateButton } from "./WorkflowElements";
import WorkflowCard from "./WorkflowCard";

/**
 * Workflow - Manages the layout, drag and drop, CRUD for workflow ACTIONS.
 */
const Workflow = ({
  workflow,
  workflowErrors,
  setActions,
  openDrawer,
  setEditAction,
}) => {
  const [cancelAnimation, setCancelAnimation] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const actionsLength = workflow?.Actions?.length ?? 0;

  // State hack that allows us to cancel DragOverlay animation.
  useEffect(() => {
    cancelAnimation && setCancelAnimation(false);
  }, [cancelAnimation]);

  return (
    <>
      <DndContext
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="mb-3 border-bottom border-superlight workflow-container d-flex flex-column align-items-center">
          <WorkflowCard
            isTrigger={true}
            action={workflow?.Trigger}
            index={-1}
            length={actionsLength}
            openCreateDrawer={openDrawer}
          />
          <Droppable
            id="main"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
            childComponent={() =>
              workflow.Actions.map((action, index) => {
                action.Index = index + 1;

                return (
                  <WorkflowCard
                    key={action.id}
                    action={action}
                    actions={workflow?.Actions}
                    errors={workflowErrors}
                    index={index + 1}
                    length={actionsLength}
                    openCreateDrawer={openDrawer}
                    openEditDrawer={(actionID, actionType, nestedAction) => {
                      openDrawer(actionID, actionType);
                      setEditAction(nestedAction || action);
                    }}
                  />
                );
              })
            }
          />
          <CreateButton onClick={openDrawer} />
        </div>
        <DragOverlay
          dropAnimation={
            cancelAnimation
              ? null
              : {
                  duration: 200,
                  easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
                }
          }
          modifiers={
            activeAction?.Type === "if-else-case" ? [snapCenterToCursor] : []
          }
        >
          {activeAction && (
            <div
              className="px-3 pt-4 pb-3 bg-white  align-items-center rounded"
              style={{
                border: "1px dashed var(--light)",
                maxWidth: 500,
              }}
            >
              <h3 className="mb-0 text-truncate">{activeAction?.Name}</h3>
              <div className="pr-2" style={{ fontSize: 14 }}>
                {activeAction?.Description}
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </>
  );

  function handleDragStart({ active }) {
    const activeAction = active?.data?.current?.action;

    document.body.style.setProperty("cursor", "grabbing", "important");

    activeAction && setActiveAction(activeAction);
  }

  function handleDragEnd(event) {
    setActiveAction(null);
    document.body.style.setProperty("cursor", "");

    const { active, over } = event;

    if (!active || !over) return setCancelAnimation(true);
    if (active?.id === over?.id) return setCancelAnimation(true);

    const isNestedDroppable =
      over.id.startsWith("Left") || over.id.startsWith("Right");
    const isMainDroppable = over.id === "main";

    if (isMainDroppable) {
      return handleDropOntoMain(active);
    }

    if (isNestedDroppable) {
      return handleDropOntoNested(active, over);
    }

    if (!isNestedDroppable && !isMainDroppable) {
      return handleDropOntoAction(active, over);
    }
  }

  // When an item is dropped onto the Main workflow droppable.
  // Adds active item to the end of the actions list.
  function handleDropOntoMain(active) {
    const result = WorkflowUtils.traverseActionsAndUpdate(workflow.Actions, {
      Type: "RemoveAndReturn",
      id: active.id,
    });

    const newActions = result.actions;
    const removedAction = result.removedAction;

    setActions([...newActions, removedAction]);
  }

  // When an item is dropped onto a True/False Actions droppable.
  // Adds active item to end of True/False Actions list.
  function handleDropOntoNested(active, over) {
    const isLeft = over.id.startsWith("Left");
    const isRight = over.id.startsWith("Right");
    const overId = isLeft
      ? over.id.replace("Left", "")
      : over.id.replace("Right", "");

    const activeResult = WorkflowUtils.traverseActionsAndUpdate(
      workflow.Actions,
      {
        Type: "RemoveAndReturn",
        id: active.id,
      }
    );

    const overResult = WorkflowUtils.traverseActionsAndUpdate(
      activeResult.actions,
      {
        Type: "Return",
        id: overId,
      }
    );

    const overAction = overResult.action;
    const removedAction = activeResult.removedAction;

    isLeft && overAction.TrueActions.push(removedAction);
    isRight && overAction.FalseActions.push(removedAction);

    setActions(overResult.actions);
  }

  // When one action is dragged onto another action.
  function handleDropOntoAction(active, over) {
    const activeIndex = active?.data?.current?.action?.Index;
    const overIndex = over?.data?.current?.action?.Index;

    if (!activeIndex || !overIndex) return;

    const droppedOnAboveAction = activeIndex >= overIndex;

    const activeResult = WorkflowUtils.traverseActionsAndUpdate(
      workflow.Actions,
      {
        Type: "RemoveAndReturn",
        id: active.id,
      }
    );

    const activeAction = activeResult.removedAction;

    const newActions = WorkflowUtils.traverseActionsAndUpdate(
      activeResult.actions,
      {
        Type: droppedOnAboveAction ? "Add" : "AddAfter",
        id: over.id,
        addAction: activeAction,
      }
    );

    setActions(newActions);
  }
};

export default Workflow;
